import React, {memo, useEffect, useState, ReactNode} from 'react';
import {
  ActivityIndicator,
  Image,
  ImageProps,
  StyleSheet,
  View,
} from 'react-native';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

interface FallbackImageProps extends Omit<ImageProps, 'source'> {
  uri?: string;
  fallback: ReactNode;
  timeout?: number;
}

const FallbackImage: React.FC<FallbackImageProps> = memo(
  ({uri, fallback, timeout = 10000, ...rest}) => {
    const [useFallback, setUseFallback] = useState(!uri);
    const [loaded, setLoaded] = useState(false);
    const {primaryColor} = getLayoutDescriber();

    useEffect(() => {
      if (!uri) return;

      const timer = setTimeout(() => {
        if (!loaded) setUseFallback(true);
      }, timeout);

      return () => clearTimeout(timer);
    }, [uri, loaded, timeout]);

    if (useFallback) {
      return <>{fallback}</>;
    }

    const imageLoader = () => {
      if (!loaded) {
        return (
          <ActivityIndicator
            color={primaryColor()}
            size={'large'}
            style={styles.imageLoader}
          />
        );
      }
      return null;
    };

    return (
      <View style={[rest?.style, styles.imageContainer]}>
        <Image
          source={{uri}}
          onLoad={() => setLoaded(true)}
          onError={() => setUseFallback(true)}
          {...rest}
        />
        {imageLoader()}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  imageLoader: {
    position: 'absolute',
  },
  imageContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default FallbackImage;
